import { ref, readonly, useContext, computed, reactive } from "@nuxtjs/composition-api";
import { useCart } from "@wemade-vsf/customer";
import { Logger } from "@wemade-vsf/core";
import { useEvent } from "@wemade-vsf/composables";
import getSveaConfigGql from "./getSveaConfig.gql";
import getEstimatedShippingMethodsGql from "./getEstimatedShippingMethods.gql";
const sveaCookieName = "vsf-svea-order";
const sveaNewsletterCookieName = "vsf-newsletter";
const state = reactive({
  sveaConfig: null
});
function useSveaOrder() {
  var _a, _b, _c, _d, _e, _f;
  const loading = ref(false);
  const error = ref({
    confirmShipping: null,
    confirmOrder: null,
    load: null,
    createOrUpdate: null,
    loadConfig: null,
    loadShippingMethods: null
  });
  const { cart, load: loadCart } = useCart();
  const { app } = useContext();
  const apiState = app.$vsf.$magento.config.state;
  const showNewsletter = (_c = (_b = (_a = app.$wm) == null ? void 0 : _a.paymentSvea) == null ? void 0 : _b.showNewsletter) != null ? _c : false;
  const sessionExpiry = 1728e5;
  const nlSignup = app.$cookies.get(sveaNewsletterCookieName);
  const signUpForNewsletter = ref((_f = (_e = (_d = app.$wm) == null ? void 0 : _d.paymentSvea) == null ? void 0 : _e.newsletterChecked) != null ? _f : true);
  if (!showNewsletter || nlSignup === false) {
    signUpForNewsletter.value = false;
  } else {
    app.$cookies.set(sveaNewsletterCookieName, signUpForNewsletter.value);
  }
  function getCookieInfo() {
    const info = app.$cookies.get(sveaCookieName);
    let result = null;
    if (info) {
      if (info.expiresAt <= new Date().getTime()) {
        app.$cookies.remove(sveaCookieName);
      } else {
        result = info;
      }
    }
    return result;
  }
  function updateCookieInfo(info) {
    app.$cookies.set(sveaCookieName, info);
  }
  const createOrUpdate = async () => {
    let result = null;
    const cookieInfo = getCookieInfo();
    if (cookieInfo) {
      const { orderId, expiresAt } = cookieInfo;
      const order = await update(orderId);
      if (order) {
        result = {
          order,
          expiresAt
        };
      }
    } else {
      result = await create();
    }
    return result;
  };
  const update = async (orderId) => {
    let result = null;
    try {
      loading.value = true;
      if (!apiState.getCartId()) {
        await loadCart({ realCart: true });
      }
      const cartId = cart.value.id;
      const { data, errors } = await app.$vsf.$magento.api.updateSveaOrder({ cartId, sveaId: orderId });
      Logger.debug("[Result]:", { data });
      if (errors) {
        const joinedErrors = errors.map((e) => e.message).join(",");
        throw new Error(joinedErrors);
      }
      result = (data == null ? void 0 : data.updateSveaOrder) || null;
      error.value.createOrUpdate = null;
    } catch (err) {
      Logger.error("useSveaOrder/update", err);
      error.value.createOrUpdate = err;
    } finally {
      loading.value = false;
    }
    return result;
  };
  const create = async () => {
    Logger.debug("useSveaOrder/create");
    let result = null;
    try {
      loading.value = true;
      if (!apiState.getCartId()) {
        await loadCart({ realCart: true });
      }
      const cartId = cart.value.id;
      const { data, errors } = await app.$vsf.$magento.api.createSveaOrder(cartId);
      Logger.debug("[Result]:", { data });
      if (errors) {
        const joinedErrors = errors.map((e) => e.message).join(",");
        throw new Error(joinedErrors);
      }
      const order = (data == null ? void 0 : data.createSveaOrder) || null;
      if (order == null ? void 0 : order.OrderId) {
        const expiresAt = new Date().getTime() + sessionExpiry;
        updateCookieInfo({ orderId: order.OrderId, expiresAt });
        result = {
          order,
          expiresAt
        };
      }
      error.value.createOrUpdate = null;
    } catch (err) {
      Logger.error("useSveaOrder/create", err);
      error.value.createOrUpdate = err;
    } finally {
      loading.value = false;
    }
    return result;
  };
  const confirmShipping = async (info) => {
    var _a2, _b2;
    Logger.debug("useSveaOrder/confirmShipping", info);
    let result = null;
    try {
      loading.value = true;
      if (!apiState.getCartId()) {
        await loadCart({ realCart: true });
      }
      const cartId = cart.value.id;
      const shippingInfo = {
        carrier: info.carrier,
        id: info.id,
        name: info.name,
        ...info.location ? { location: {
          id: (_a2 = info.location.id) != null ? _a2 : "",
          name: (_b2 = info.location.name) != null ? _b2 : "",
          ...info.location.address ? { address: info.location.address } : {}
        } } : {},
        orderId: info.orderId,
        totalShippingFee: info.totalShippingFee,
        price: info.price,
        shippingFee: info.shippingFee,
        postalCode: info.postalCode
      };
      const { data } = await app.$vsf.$magento.api.confirmSveaShipping({ cartId, shippingInfo });
      result = (data == null ? void 0 : data.confirmSveaShipping) || null;
      error.value.confirmShipping = null;
    } catch (err) {
      Logger.debug("useSveaOrder/confirmShipping", err);
      error.value.confirmShipping = err;
    } finally {
      loading.value = false;
    }
    return result;
  };
  const confirmOrder = async (hash) => {
    Logger.debug("useSveaOrder/confirmOrder", hash);
    let result = null;
    loading.value = true;
    try {
      const cookieInfo = app.$cookies.get(sveaCookieName);
      if (cookieInfo == null ? void 0 : cookieInfo.orderId) {
        const { data, errors } = await app.$vsf.$magento.api.confirmSveaOrder({ sveaId: cookieInfo.orderId, hash });
        Logger.debug("[Result]:", { data, errors });
        result = (data == null ? void 0 : data.confirmSveaOrder) || null;
        if (errors) {
          const joinedErrors = errors.map((e) => e.message).join(",");
          throw new Error(joinedErrors);
        }
        if (showNewsletter && signUpForNewsletter.value) {
          useEvent("checkout:newsletter:signup", {
            email: result.email
          });
        }
      }
    } catch (err) {
      Logger.error("useSveaOrder/confirmOrder", err);
      error.value.confirmOrder = err;
    } finally {
      app.$cookies.remove(sveaCookieName);
      app.$cookies.remove(sveaNewsletterCookieName);
      loading.value = false;
    }
    return result;
  };
  const load = async () => {
    let result = null;
    return result;
  };
  const canCheckout = computed(() => {
    var _a2, _b2, _c2;
    if (!cart.value || !((_a2 = cart.value.items) == null ? void 0 : _a2.length))
      return false;
    return ((_c2 = (_b2 = cart.value.prices) == null ? void 0 : _b2.subtotal_excluding_tax) == null ? void 0 : _c2.value) > 0;
  });
  const updateNewsletter = (value) => {
    signUpForNewsletter.value = value;
    app.$cookies.set(sveaNewsletterCookieName, value);
  };
  async function loadConfig() {
    var _a2, _b2, _c2, _d2, _e2, _f2, _g;
    if (state.sveaConfig)
      return state.sveaConfig;
    try {
      loading.value = true;
      const { data } = await app.$vsf.$magento.api.customQuery({ query: getSveaConfigGql });
      state.sveaConfig = {
        default_country: (_b2 = (_a2 = data == null ? void 0 : data.storeConfig) == null ? void 0 : _a2.shipping_origin_country_id) != null ? _b2 : "NO",
        default_postcode: (_d2 = (_c2 = data == null ? void 0 : data.storeConfig) == null ? void 0 : _c2.shipping_origin_postcode) != null ? _d2 : "",
        default_shipping_method: (_e2 = data == null ? void 0 : data.storeConfig.svea) == null ? void 0 : _e2.default_shipping_method,
        svea_shipping_active: (_g = (_f2 = data == null ? void 0 : data.storeConfig.svea) == null ? void 0 : _f2.svea_shipping_active) != null ? _g : false
      };
      error.value.loadConfig = null;
    } catch (err) {
      error.value.loadConfig = err;
    } finally {
      loading.value = false;
    }
    return state.sveaConfig;
  }
  async function getEstimatedShippingMethods(cartId, address) {
    var _a2, _b2;
    let result = [];
    try {
      loading.value = true;
      const { data } = await app.$vsf.$magento.api.customQuery({
        query: getEstimatedShippingMethodsGql,
        queryVariables: {
          cartId,
          address
        }
      });
      result = (_b2 = (_a2 = data == null ? void 0 : data.WmEstimateShippingMethods) == null ? void 0 : _a2.items) != null ? _b2 : [];
      error.value.loadShippingMethods = null;
    } catch (err) {
      error.value.loadShippingMethods = err;
    } finally {
      loading.value = false;
    }
    return result;
  }
  return {
    loading: readonly(loading),
    error: readonly(error),
    canCheckout,
    sessionExpiry,
    signUpForNewsletter,
    createOrUpdate,
    confirmShipping,
    confirmOrder,
    load,
    update,
    create,
    updateNewsletter,
    loadConfig,
    getEstimatedShippingMethods
  };
}
import { SveaConfig } from "./useSveaOrder";
export {
  SveaConfig,
  useSveaOrder
};
